import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { ConfigProvider, Empty, Tabs, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useStore } from '../../../../store/rootStore';
import { formatDateTime, formatRelativeTime, getDirection } from '../../../../helpers';
import { getNotificationTitle, isNotificationOfType } from '../../helpers';
import styles from './List.module.scss';
const statusIconMap = {
    success: React.createElement(CheckCircleOutlined, null),
    warning: React.createElement(WarningOutlined, null),
    error: React.createElement(ExclamationCircleOutlined, null),
};
const NotificationList = ({ selectedId, onNotificationClick }) => {
    const { notificationsStore, channels, timeFormat } = useStore();
    const { t, i18n } = useTranslation();
    const [isUnreadOnly, setIsUnreadOnly] = useState(false);
    const notifications = notificationsStore.notifications
        .filter((notification) => !isUnreadOnly || !notification.read)
        .sort((a, b) => b.date - a.date);
    const notificationList = notifications.length === 0 ? (React.createElement(Empty, { className: styles.empty, image: Empty.PRESENTED_IMAGE_SIMPLE, description: t(isUnreadOnly ? 'noUnreadNotifications' : 'noNotifications') })) : (React.createElement("ul", { className: styles.notifications }, notifications.map((notification) => {
        var _a;
        const status = t(`notificationStatus.${notification.status}`);
        const title = getNotificationTitle(notification, status);
        let channelLabel = '';
        if (isNotificationOfType(notification, 'channel')) {
            const { channel, accountId } = notification.source;
            const account = channels.getAccount({ name: channel, id: accountId });
            let accountTitle = accountId;
            if ((_a = account === null || account === void 0 ? void 0 : account.metadata) === null || _a === void 0 ? void 0 : _a.accountTitle)
                accountTitle = account.metadata.accountTitle;
            if (account === null || account === void 0 ? void 0 : account.title)
                accountTitle = account.title;
            channelLabel = t(`${channel}Channel`, { defaultValue: channel });
            if (accountTitle)
                channelLabel += ` · ${accountTitle}`;
        }
        const date = new Date(notification.date);
        const fullDate = formatDateTime(notification.date, i18n.language, timeFormat, 'full');
        return (React.createElement("li", { key: notification._id },
            React.createElement("button", { type: "button", className: styles.notificationItem, "aria-current": notification._id === selectedId ? 'true' : undefined, onClick: (event) => onNotificationClick(notification._id, event.currentTarget) },
                React.createElement(Tooltip, { title: status },
                    React.createElement("span", { className: classNames(styles.status, styles[notification.status]), role: "img", "aria-label": status },
                        React.createElement("span", { "aria-hidden": "true" }, statusIconMap[notification.status]))),
                React.createElement("span", { className: styles.content, style: { textAlign: getDirection() === 'rtl' ? 'right' : 'left' } },
                    React.createElement("span", { className: styles.titleContainer },
                        React.createElement("span", { className: classNames(styles.title, {
                                [styles.unread]: !notification.read,
                            }), dir: "auto" }, title),
                        !notification.read && (React.createElement("span", { className: styles.unreadDot, role: "img", "aria-label": t('unread') }))),
                    notification.description !== title && (React.createElement("span", { className: styles.description, dir: "auto" }, notification.description)),
                    React.createElement("span", { className: styles.metadata },
                        React.createElement(Tooltip, { title: channelLabel },
                            React.createElement("span", { className: styles.channel }, channelLabel)),
                        React.createElement(Tooltip, { title: fullDate },
                            React.createElement("time", { dateTime: date.toISOString(), className: styles.date, dir: "auto" }, formatRelativeTime(notification.date, timeFormat, t))))))));
    })));
    return (React.createElement(ConfigProvider, { direction: getDirection() },
        React.createElement(Tabs, { className: styles.tabs, activeKey: isUnreadOnly ? 'unread' : 'all', onChange: (key) => setIsUnreadOnly(key === 'unread'), destroyInactiveTabPane: true, items: [
                { key: 'all', label: t('all'), children: notificationList },
                { key: 'unread', label: t('unread'), children: notificationList },
            ] })));
};
export default observer(NotificationList);
